import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Castle, LockKeyhole, LockKeyholeOpen, RotateCcw, Swords, Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import LightColorControl from "@/dungeons/lighting/LightColorControl.tsx";
import LightValueSlider from "@/dungeons/lighting/LightValueSlider.tsx";
import {DEFAULT_LIGHT_COLOR_STRENGTH, LIGHT_PRESET_LABELS, LIGHT_PRESETS} from "@/dungeons/lighting/LightPresets.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useEncounterLights} from "@/encounter/lights/EncounterLightHooks.ts";
import type {EncounterLight, EncounterLightOverride} from "@/encounter/lights/EncounterLights.ts";
import type {DungeonLight} from "@/dungeons/model/DungeonGeometry.ts";
import type {EffectiveLight} from "@/tabletop/visibility/LightGeometry.ts";
import LightAnimationControl from "@/dungeons/lighting/LightAnimationControl.tsx";
import {useMapPopoverLayout} from "@/tabletop/useMapPopoverLayout.ts";

export default function EncounterLightPopover({light, encounterLight, dungeonLight, hasOverride,
    locked, onToggleLock, anchor, viewport, onClose}: {
    light: EffectiveLight; encounterLight?: EncounterLight; dungeonLight?: DungeonLight; hasOverride: boolean;
    locked: boolean; onToggleLock: () => void;
    anchor: {x: number; y: number}; viewport: {width: number; height: number}; onClose: () => void;
}) {
    const {saveOverride, deleteOverride, saveLight, deleteLight, previewLight, clearPreview} = useEncounterLights();
    const {combatants} = useCombatants();
    const [draft, setDraft] = useState({enabled: light.enabled, rangeMeters: light.rangeMeters,
        intensity: light.intensity, colorStrength: light.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH,
        color: light.color, animation: light.animation ?? null,
        combatantId: encounterLight?.combatantId ?? "", preset: encounterLight?.preset ?? "CUSTOM"});
    const draftRef = useRef(draft);
    const lightRef = useRef(light);
    const encounterLightRef = useRef(encounterLight);
    const combatantsRef = useRef(combatants);
    useLayoutEffect(() => {
        lightRef.current = light;
        encounterLightRef.current = encounterLight;
        combatantsRef.current = combatants;
    }, [light, encounterLight, combatants]);
    const saveQueue = useRef<Promise<void>>(Promise.resolve());
    const saveVersion = useRef(0);
    const active = useRef(false);
    const busyRef = useRef(false);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    useEffect(() => {
        active.current = true;
        return () => {active.current = false; clearPreview();};
    }, [clearPreview]);
    const {panelRef, contentRef, style} = useMapPopoverLayout({anchor, viewport, width: 240});
    const toEncounterLight = (value: typeof draft, position?: {x: number; y: number}): EncounterLight => ({
        ...encounterLightRef.current!, ...value,
        preset: value.preset as EncounterLight["preset"], combatantId: value.combatantId || null,
        position: position ?? encounterLightRef.current!.position,
        levelId: combatantsRef.current.find(item => item.id === value.combatantId)?.levelId
            ?? combatantsRef.current.find(item => item.id === encounterLightRef.current?.combatantId)?.levelId
            ?? encounterLightRef.current!.levelId});
    const toOverride = (value: typeof draft): EncounterLightOverride => ({
        lightId: light.id, enabled: value.enabled, rangeMeters: value.rangeMeters,
        intensity: value.intensity, colorStrength: value.colorStrength,
        color: value.color, position: lightRef.current.origin,
        animation: value.animation,
    });
    const previewDraft = (changes: Partial<typeof draft>) => {
        if (locked || busyRef.current) return draftRef.current;
        const next = {...draftRef.current, ...changes};
        draftRef.current = next;
        setDraft(next);
        previewLight(encounterLight
            ? {id: light.id, values: {enabled: next.enabled, rangeMeters: next.rangeMeters,
                intensity: next.intensity, colorStrength: next.colorStrength, color: next.color, animation: next.animation,
                combatantId: next.combatantId || null, preset: next.preset as EncounterLight["preset"]}}
            : {lightId: light.id, values: {enabled: next.enabled, rangeMeters: next.rangeMeters,
                intensity: next.intensity, colorStrength: next.colorStrength, color: next.color, animation: next.animation}});
        return next;
    };
    const commitDraft = (changes: Partial<typeof draft>, position?: {x: number; y: number}) => {
        if (locked || busyRef.current) return;
        const next = previewDraft(changes);
        const version = ++saveVersion.current;
        setSaving(true);
        setError(undefined);
        saveQueue.current = saveQueue.current.catch(() => undefined).then(async () => {
            try {
                if (encounterLightRef.current) await saveLight(toEncounterLight(next, position));
                else await saveOverride(toOverride(next));
                if (active.current && version === saveVersion.current) clearPreview();
            } catch {
                if (active.current && version === saveVersion.current) {
                    clearPreview();
                    setError("Das Licht konnte nicht gespeichert werden.");
                }
            } finally {
                if (active.current && version === saveVersion.current) setSaving(false);
            }
        });
    };
    const resetSelected = () => {
        if (locked || !dungeonLight || busyRef.current) return;
        busyRef.current = true;
        saveVersion.current++;
        setBusy(true);
        setError(undefined);
        void saveQueue.current.catch(() => undefined).then(() => deleteOverride(light.id))
            .then(() => {
                clearPreview();
                const defaults = {...draftRef.current, enabled: dungeonLight.enabled,
                    rangeMeters: dungeonLight.rangeMeters, intensity: dungeonLight.intensity,
                    colorStrength: dungeonLight.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH,
                    color: dungeonLight.color, animation: dungeonLight.animation ?? null};
                draftRef.current = defaults;
                if (active.current) setDraft(defaults);
            })
            .catch(() => {if (active.current) setError("Der Licht-Override konnte nicht zurückgesetzt werden.");})
            .finally(() => {
                busyRef.current = false;
                if (active.current) {setBusy(false); setSaving(false);}
            });
    };
    const deleteSelected = () => {
        if (locked || busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setError(undefined);
        void saveQueue.current.catch(() => undefined).then(() => deleteLight(light.id))
            .then(onClose)
            .catch(() => setError("Das Licht konnte nicht gelöscht werden."))
            .finally(() => {busyRef.current = false; if (active.current) setBusy(false);});
    };
    const selectPreset = (preset: keyof typeof LIGHT_PRESETS) => commitDraft({...LIGHT_PRESETS[preset], preset});
    const sourceLabel = light.source === "encounter" ? "Encounter-Licht" : "Dungeon-Licht";
    const lockLabel = `${sourceLabel} ${locked ? "entsperren" : "sperren"}`;
    return <div className="pointer-events-none absolute inset-0 z-10">
        <div ref={panelRef} role="dialog" aria-label="Licht bearbeiten"
            className="pointer-events-auto absolute overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-xl"
            style={style}>
            <div ref={contentRef} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h3 aria-label={sourceLabel} title={sourceLabel} className="flex shrink-0 items-center">
                        {light.source === "encounter" ?
                            <Swords className="size-4 text-destructive" aria-hidden="true"/> :
                            <Castle className="size-4 text-success" aria-hidden="true"/>}
                    </h3>
                    <div className="flex items-center gap-1">
                        <label className="flex items-center gap-1.5 text-xs">
                            Aktiv <Switch aria-label="Licht eingeschaltet" checked={draft.enabled}
                                disabled={locked}
                                onCheckedChange={enabled => commitDraft({enabled})}/>
                        </label>
                        <Button type="button" size="icon-sm" variant="ghost"
                            aria-label={lockLabel} title={lockLabel}
                            disabled={busy} onClick={onToggleLock}>
                            {locked ? <LockKeyhole/> : <LockKeyholeOpen/>}
                        </Button>
                        {!encounterLight && hasOverride && <Button type="button" size="icon-sm" variant="ghost"
                            aria-label="Licht-Override zurücksetzen" title="Licht-Override zurücksetzen"
                            disabled={locked || busy} onClick={resetSelected}>
                            <RotateCcw/>
                        </Button>}
                        {encounterLight && <Button type="button" size="icon-sm" variant="ghost"
                            className="text-destructive hover:text-destructive"
                            aria-label="Licht löschen" title="Licht löschen"
                            disabled={locked || busy} onClick={deleteSelected}>
                            <Trash2/>
                        </Button>}
                    </div>
                </div>
                {encounterLight && <label className="block space-y-1 text-xs">Preset
                    <select value={draft.preset} disabled={locked} onChange={event => {
                        const preset = event.target.value as keyof typeof LIGHT_PRESETS;
                        if (preset in LIGHT_PRESETS) selectPreset(preset);
                    }} className="h-8 w-full rounded-md border border-input bg-surface px-2 text-xs">
                        {Object.entries(LIGHT_PRESET_LABELS).map(([value, label]) =>
                            <option key={value} value={value}>{label}</option>)}
                    </select>
                </label>}
                <LightValueSlider label="Reichweite" value={draft.rangeMeters} min={0.5} max={100} step={0.5}
                    compact disabled={locked}
                    format={value => `${value.toFixed(1)} m`}
                    onPreview={rangeMeters => previewDraft({rangeMeters})}
                    onCommit={rangeMeters => commitDraft({rangeMeters})}/>
                <LightValueSlider label="Helligkeit" value={draft.intensity} min={0} max={1} step={0.01}
                    compact disabled={locked}
                    format={value => value.toFixed(2)}
                    onPreview={intensity => previewDraft({intensity})}
                    onCommit={intensity => commitDraft({intensity})}/>
                <LightValueSlider label="Farbstärke" value={draft.colorStrength} min={0} max={1} step={0.01}
                    compact disabled={locked}
                    format={value => value.toFixed(2)}
                    onPreview={colorStrength => previewDraft({colorStrength})}
                    onCommit={colorStrength => commitDraft({colorStrength})}/>
                <LightColorControl key={locked ? "locked" : "unlocked"} color={draft.color} compact disabled={locked}
                    onPreview={color => previewDraft({color})} onCommit={color => commitDraft({color})}/>
                <LightAnimationControl animation={draft.animation} compact disabled={locked}
                    onPreview={animation => previewDraft({animation})} onCommit={animation => commitDraft({animation})}/>
                {encounterLight && <label className="block space-y-1 text-xs">An Combatant binden
                    <select value={draft.combatantId} disabled={locked} onChange={event => commitDraft({
                        combatantId: event.target.value}, lightRef.current.origin)}
                        className="h-8 w-full rounded-md border border-input bg-surface px-2 text-xs">
                        <option value="">Nicht gebunden / hier ablegen</option>
                        {combatants.map(combatant => <option key={combatant.id} value={combatant.id}>{combatant.name}</option>)}
                    </select>
                </label>}
                {saving && <p className="pt-1 text-xs text-muted-foreground">Wird gespeichert…</p>}
                {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            </div>
        </div>
    </div>;
}
